import { useRef } from 'react';
import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import { useForgotPasswordMutation } from '@/query/auth.query';

import type { ForgotPasswordRequestFormValues } from '@/schemas/auth.schema';

import { useFormikError } from '@/lib/error';

import { ForgotPasswordRequestFormProvider } from '@/formik/auth.formik';

interface ForgotPasswordRequestProps {
  onSuccess: (username: string) => void;
  onBack: () => void;
}

const SubmitButton = ({ isPending }: { isPending: boolean }) => {
  const { isValid } = useFormikContext();
  return (
    <Button type="submit" className="w-full" disabled={isPending || !isValid}>
      {isPending ? 'Sending...' : 'Send reset code'}
    </Button>
  );
};

const RequestForm = ({ isPending, onBack }: { isPending: boolean; onBack: () => void }) => {
  const { getFieldProps } = useFormikContext<ForgotPasswordRequestFormValues>();
  const hasError = useFormikError();

  return (
    <>
      <div className="grid gap-3">
        <Field aria-invalid={hasError('username')}>
          <RequiredLabel htmlFor="username">Email</RequiredLabel>
          <Input
            id="username"
            type="email"
            placeholder="m@example.com"
            {...getFieldProps('username')}
            aria-invalid={hasError('username')}
          />
          <ErrorField name="username" />
        </Field>
      </div>

      <SubmitButton isPending={isPending} />
      <Button type="button" variant="ghost" className="w-full" onClick={onBack}>
        Back to login
      </Button>
    </>
  );
};

export const ForgotPasswordRequest = ({ onSuccess, onBack }: ForgotPasswordRequestProps) => {
  const submittedUsernameRef = useRef('');

  const forgotMutation = useForgotPasswordMutation({
    onSuccess: () => onSuccess(submittedUsernameRef.current),
  });

  return (
    <ForgotPasswordRequestFormProvider
      onSubmit={values => {
        submittedUsernameRef.current = values.username;
        forgotMutation.mutate(values.username);
      }}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Forgot your password?</h1>
        <p className="text-muted-foreground text-sm text-balance">
          Enter your email to receive a password reset code.
        </p>
      </div>

      <div className="grid gap-4 py-4">
        <RequestForm isPending={forgotMutation.isPending} onBack={onBack} />
      </div>
    </ForgotPasswordRequestFormProvider>
  );
};
